import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Card, CardBody, Button, Label } from "reactstrap";
import { FiBox, FiPieChart, FiRefreshCcw, FiActivity } from "react-icons/fi";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";

// MUI Imports
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const InventoryAnalytics = () => {
    const dispatch = useDispatch();
    const [rawResponse, setRawResponse] = useState(null);

    // Filter States
    const [filters, setFilters] = useState({
        filter: { value: "all", label: "All Time Stock" },
        from_date: dayjs(),
        to_date: dayjs()
    });

    const filterOptions = [
        { value: "all", label: "All Time Stock" },
        { value: "today", label: "Today" },
        { value: "week", label: "Last Week" },
        { value: "month", label: "Last Month" },
        { value: "3months", label: "Last 3 Months" },
        { value: "custom", label: "Custom Range" }
    ];

    const COLORS = ['#0f7c7d', '#f16436', '#FFB547', '#243b20', '#6f42c1'];

    const loadAnalytics = async (currentFilters = filters) => {
        dispatch(loadingTrue());
        try {
            const queryParams = new URLSearchParams({
                filter: currentFilters.filter.value,
                from_date: currentFilters.from_date ? dayjs(currentFilters.from_date).format('YYYY-MM-DD') : "",
                to_date: currentFilters.to_date ? dayjs(currentFilters.to_date).format('YYYY-MM-DD') : ""
            }).toString();

            const res = await Handler({ method: "GET", url: `/report/inventory_report.php?${queryParams}` });
            if (res?.success) {
                setRawResponse(res);
            }
        } catch (err) {
            toast.error("Sync Failed");
        } finally {
            dispatch(loadingFalse());
        }
    };

    useEffect(() => {
        if (filters.filter.value !== 'custom') {
            loadAnalytics();
        }
    }, [filters.filter.value]);

    const handlePeriodChange = (selectedOption) => {
        const type = selectedOption.value;
        let from = dayjs();
        let to = dayjs();

        if (type === 'week') from = dayjs().subtract(7, 'day');
        else if (type === 'month') from = dayjs().subtract(1, 'month');
        else if (type === '3months') from = dayjs().subtract(3, 'month');

        const newFilters = { ...filters, filter: selectedOption, from_date: from, to_date: to };
        setFilters(newFilters);

        if (type !== 'custom') {
            loadAnalytics(newFilters);
        }
    };

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            minHeight: "45px",
            boxShadow: "none",
            '&:hover': { border: '1px solid #0f7c7d' }
        })
    };

    // 🔥 Chart Logic Updated for your API structure
    const chartData = useMemo(() => {
        const items = rawResponse?.data || [];
        
        // Bar Chart: Stock Levels
        const trendData = items.map(item => ({
            name: item.product?.name || 'N/A',
            stock: item.stock?.kg || 0
        }));

        // Pie Chart: Distribution
        const stockDist = items.map(item => ({
            name: item.product?.name || "Unknown",
            value: item.stock?.kg || 0
        }));

        return { stockDist, trendData };
    }, [rawResponse]);

    const summary = rawResponse?.summary || {};

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-3 bg-light min-vh-100">
                {/* Header Area */}
                <div className="d-md-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h4 className="fw-bold m-0 text-dark">Inventory Insights</h4>
                        <p className="text-muted small m-0">Live Stock Distribution & Volume Analysis</p>
                    </div>
                    <div className="d-flex gap-2 mt-3 mt-md-0 align-items-center">
                        <div style={{ width: '200px' }}>
                            <Select
                                options={filterOptions}
                                value={filters.filter}
                                onChange={handlePeriodChange}
                                styles={customSelectStyles}
                            />
                        </div>
                        <Button color="white" className="shadow-sm border rounded-pill px-3 py-2" onClick={() => loadAnalytics()}>
                            <FiRefreshCcw />
                        </Button>
                    </div>
                </div>

                {/* MUI Custom Range Filter */}
                {filters.filter.value === 'custom' && (
                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <CardBody className="p-4">
                            <Row className="g-3 align-items-end">
                                <Col md={4}>
                                    <Label className="x-small fw-bold text-muted d-block mb-2 text-uppercase">Start Date</Label>
                                    <DatePicker
                                        disableFuture
                                        format="DD/MM/YYYY"
                                        value={filters.from_date}
                                        onChange={(val) => setFilters({ ...filters, from_date: val })}
                                        sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#fff' } }}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label className="x-small fw-bold text-muted d-block mb-2 text-uppercase">End Date</Label>
                                    <DatePicker
                                        disableFuture
                                        format="DD/MM/YYYY"
                                        value={filters.to_date}
                                        minDate={filters.from_date}
                                        onChange={(val) => setFilters({ ...filters, to_date: val })}
                                        sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#fff' } }}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Button color="primary" block className="fw-bold rounded-3 shadow-sm" style={{ height: '54px', backgroundColor: '#0f7c7d', border: 'none' }} onClick={() => loadAnalytics()}>
                                        Sync Insights
                                    </Button>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                )}

                {/* 🔥 Top Summary Cards using API Summary keys */}
                <Row className="g-3 mb-4">
                    <StatCard title="Gross Volume (KG)" value={`${summary.total_stock_kg?.toLocaleString() || 0}`} icon={<FiBox />} color="#0f7c7d" bg="#0f7c7d15" />
                    <StatCard title="Volume (Quintal)" value={`${summary.total_stock_quintal?.toLocaleString() || 0}`} icon={<FiActivity />} color="#f16436" bg="#f1643615" />
                    <StatCard title="Unique Products" value={summary.total_products || 0} icon={<FiPieChart />} color="#243b20" bg="#243b2015" />
                </Row>

                {/* Charts Section */}
                <Row className="g-4">
                    <Col lg={7}>
                        <Card className="border-0 shadow-sm rounded-4 h-100">
                            <CardBody className="p-4">
                                <h6 className="fw-bold mb-4 text-uppercase small text-muted">Stock Levels by Product</h6>
                                <div style={{ width: '100%', height: 350 }}>
                                    {chartData.trendData.length > 0 ? (
                                        <ResponsiveContainer>
                                            <BarChart data={chartData.trendData}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#666' }} />
                                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#666' }} />
                                                <Tooltip cursor={{ fill: '#f8f9fa' }} contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                                <Bar dataKey="stock" fill="#0f7c7d" radius={[6, 6, 0, 0]} barSize={40} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : <div className="h-100 d-flex align-items-center justify-content-center text-muted italic">No data to visualize</div>}
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg={5}>
                        <Card className="border-0 shadow-sm rounded-4 h-100">
                            <CardBody className="p-4">
                                <h6 className="fw-bold mb-4 text-uppercase small text-muted">Stock Share (%)</h6>
                                <div style={{ width: '100%', height: 350 }}>
                                    {chartData.stockDist.length > 0 ? (
                                        <ResponsiveContainer>
                                            <PieChart>
                                                <Pie
                                                    data={chartData.stockDist}
                                                    innerRadius={80}
                                                    outerRadius={110}
                                                    paddingAngle={5}
                                                    dataKey="value"
                                                    animationDuration={1500}
                                                >
                                                    {chartData.stockDist.map((_, index) => <Cell key={index} fill={COLORS[index % COLORS.length]} stroke="none" />)}
                                                </Pie>
                                                <Tooltip />
                                                <Legend verticalAlign="bottom" height={36} iconType="circle" />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    ) : <div className="h-100 d-flex align-items-center justify-content-center text-muted italic">No distribution data</div>}
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        </LocalizationProvider>
    );
};

const StatCard = ({ title, value, icon, color, bg }) => (
    <Col md={4}>
        <Card className="border-0 shadow-sm rounded-4 overflow-hidden h-100 bg-white">
            <CardBody className="d-flex align-items-center gap-3 p-4">
                <div style={{
                    width: '55px', height: '55px', borderRadius: '16px',
                    backgroundColor: bg, color: color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px'
                }}>
                    {icon}
                </div>
                <div>
                    <p className="m-0 text-muted x-small fw-bold text-uppercase ls-1">{title}</p>
                    <h3 className="m-0 fw-bold" style={{ color: '#2c3e50' }}>{value}</h3>
                </div>
            </CardBody>
        </Card>
    </Col>
);

export default InventoryAnalytics;